import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Code, 
  Palette, 
  Database, 
  Globe, 
  Zap, 
  Star,
  Award,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Target,
  Layers,
  Cpu,
  Smartphone,
  Monitor,
  GitBranch,
  Cloud,
  Settings,
  Briefcase,
  Users,
  Trophy
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" }
};

const staggerChildren = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const floatingAnimation = {
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const skillCategories = [
    { id: 'all', name: 'All Skills', icon: Star },
    { id: 'frontend', name: 'Frontend', icon: Monitor },
    { id: 'styling', name: 'Styling', icon: Palette },
    { id: 'backend', name: 'Backend', icon: Database },
    { id: 'tools', name: 'Tools', icon: Settings }
  ];

  const skills = [
    {
      category: 'frontend',
      name: 'React',
      level: 95,
      icon: Code,
      description: 'Building dynamic, component-based user interfaces with hooks, context, and modern patterns',
      experience: '3+ years',
      projects: 15,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      category: 'frontend',
      name: 'JavaScript',
      level: 92,
      icon: Zap,
      description: 'ES6+, async/await, DOM manipulation, and modern JavaScript development practices',
      experience: '3+ years',
      projects: 20,
      color: 'from-yellow-500 to-orange-500'
    },
    {
      category: 'frontend',
      name: 'TypeScript',
      level: 88,
      icon: Code,
      description: 'Type-safe development, interfaces, generics, and advanced TypeScript patterns',
      experience: '2+ years',
      projects: 12,
      color: 'from-blue-600 to-indigo-600'
    },
    {
      category: 'frontend',
      name: 'HTML5',
      level: 96,
      icon: Globe,
      description: 'Semantic markup, accessibility, SEO optimization, and modern HTML standards',
      experience: '4+ years',
      projects: 25,
      color: 'from-orange-500 to-red-500'
    },
    {
      category: 'frontend',
      name: 'CSS3',
      level: 94,
      icon: Palette,
      description: 'Flexbox, Grid, animations, responsive design, and modern CSS techniques',
      experience: '4+ years',
      projects: 25,
      color: 'from-pink-500 to-purple-500'
    },
    {
      category: 'styling',
      name: 'Tailwind CSS',
      level: 90,
      icon: Layers,
      description: 'Utility-first CSS framework for rapid UI development and consistent design systems',
      experience: '2+ years',
      projects: 18,
      color: 'from-teal-500 to-green-500'
    },
    {
      category: 'frontend',
      name: 'Redux',
      level: 85,
      icon: Database,
      description: 'State management, Redux Toolkit, middleware, and complex application state handling',
      experience: '2+ years',
      projects: 8,
      color: 'from-purple-600 to-pink-600'
    },
    {
      category: 'backend',
      name: 'Node.js',
      level: 82,
      icon: Cpu,
      description: 'Server-side JavaScript, Express.js, API development, and backend architecture',
      experience: '2+ years',
      projects: 10,
      color: 'from-green-600 to-emerald-600'
    },
    {
      category: 'tools',
      name: 'Git',
      level: 88,
      icon: GitBranch,
      description: 'Version control, branching strategies, collaboration, and code management',
      experience: '3+ years',
      projects: 30,
      color: 'from-gray-600 to-slate-600'
    },
    {
      category: 'tools',
      name: 'Netlify',
      level: 85,
      icon: Cloud,
      description: 'Deployment, hosting, CI/CD, and modern web application delivery',
      experience: '2+ years',
      projects: 15,
      color: 'from-cyan-500 to-blue-500'
    }
  ];

  const achievements = [
    {
      icon: Trophy,
      title: '50+ Projects',
      description: 'Successfully delivered',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: Users,
      title: '100% Client',
      description: 'Satisfaction rate',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: TrendingUp,
      title: '3+ Years',
      description: 'Professional experience',
      color: 'from-blue-500 to-purple-500'
    },
    {
      icon: Award,
      title: 'Modern Stack',
      description: 'Latest technologies',
      color: 'from-pink-500 to-red-500'
    }
  ];

  const filteredSkills = activeCategory === 'all' 
    ? skills 
    : skills.filter(skill => skill.category === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
        
        {/* Floating Code Elements */}
        <div className="absolute inset-0">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-white/10 font-mono text-sm"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [-20, 20, -20],
                opacity: [0.1, 0.3, 0.1],
              }}
              transition={{
                duration: 4 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            >
              {['</>', '{}', '[]', '()', '=>', '&&', '||'][Math.floor(Math.random() * 7)]}
            </motion.div>
          ))}
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        
        {/* Header Section */}
        <motion.section 
          className="text-center mb-20"
          initial="initial"
          animate="animate"
          variants={staggerChildren}
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8"
            variants={fadeInUp}
          >
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-white font-medium text-sm">Technical Expertise</span>
          </motion.div>

          <motion.h1
            className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
            variants={fadeInUp}
          >
            <span className="text-white">My </span>
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              Skills
            </span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed"
            variants={fadeInUp}
          >
            A comprehensive toolkit of modern technologies and frameworks for building exceptional web experiences
          </motion.p>

          {/* Achievements Grid */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-16"
            variants={staggerChildren}
          >
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center"
                variants={fadeInUp}
                whileHover={{ y: -5, scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <div className={`bg-gradient-to-r ${achievement.color} w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4`}>
                  <achievement.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-lg font-bold text-white mb-1">{achievement.title}</div>
                <div className="text-sm text-slate-300">{achievement.description}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        {/* Category Filter */}
        <motion.section 
          className="mb-16"
          initial="initial"
          animate="animate"
          variants={staggerChildren}
        >
          <motion.div 
            className="flex flex-wrap justify-center gap-4"
            variants={fadeInUp}
          >
            {skillCategories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <category.icon className="w-4 h-4" />
                {category.name}
              </motion.button>
            ))}
          </motion.div>
        </motion.section>

        {/* Skills Grid */}
        <motion.section
          initial="initial"
          animate="animate"
          variants={staggerChildren}
          className="mb-20"
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredSkills.map((skill, index) => (
              <motion.div
                key={skill.name}
                className="group bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 hover:bg-white/15 transition-all duration-500"
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                {/* Skill Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className={`bg-gradient-to-r ${skill.color} w-14 h-14 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <skill.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-white">{skill.level}%</div>
                    <div className="text-xs text-slate-300">Proficiency</div>
                  </div>
                </div>

                {/* Skill Name */}
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-purple-300 transition-colors">
                  {skill.name}
                </h3>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="bg-white/20 rounded-full h-2 overflow-hidden">
                    <motion.div
                      className={`bg-gradient-to-r ${skill.color} h-full rounded-full`}
                      initial={{ width: 0 }}
                      animate={{ width: `${skill.level}%` }}
                      transition={{ duration: 1.5, delay: index * 0.1 }}
                    />
                  </div>
                </div>

                {/* Description */}
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  {skill.description}
                </p>

                {/* Stats */}
                <div className="flex justify-between items-center text-xs text-slate-400 mb-4">
                  <div className="flex items-center gap-1">
                    <Briefcase className="w-3 h-3" />
                    {skill.experience}
                  </div>
                  <div className="flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    {skill.projects} projects
                  </div>
                </div>

                {/* Skill Level Badge */}
                <div className="flex justify-center">
                  <span className={`inline-flex items-center gap-1 bg-gradient-to-r ${skill.color} px-3 py-1 rounded-full text-white text-xs font-semibold`}>
                    <CheckCircle className="w-3 h-3" />
                    {skill.level >= 90 ? 'Expert' : skill.level >= 80 ? 'Advanced' : 'Intermediate'}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Experience Timeline */}
        <motion.section 
          className="mb-20"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          <motion.div className="text-center mb-16" variants={fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Professional Journey
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              My growth and evolution as a frontend developer over the years
            </p>
          </motion.div>

          <motion.div 
            className="max-w-4xl mx-auto"
            variants={staggerChildren}
          >
            {[
              {
                year: '2021',
                title: 'Started Frontend Journey',
                description: 'Began learning HTML, CSS, and JavaScript fundamentals',
                icon: Sparkles,
                color: 'from-green-500 to-emerald-500'
              },
              {
                year: '2022',
                title: 'React & Modern Tools',
                description: 'Mastered React, Redux, and modern development workflows',
                icon: Code,
                color: 'from-blue-500 to-cyan-500'
              },
              {
                year: '2023',
                title: 'Professional Projects',
                description: 'Delivered multiple client projects and expanded skill set',
                icon: Briefcase,
                color: 'from-purple-500 to-pink-500'
              },
              {
                year: '2024',
                title: 'Advanced Expertise',
                description: 'TypeScript, advanced patterns, and leadership in projects',
                icon: Trophy,
                color: 'from-yellow-500 to-orange-500'
              }
            ].map((milestone, index) => (
              <motion.div
                key={index}
                className="flex items-center gap-8 mb-12 last:mb-0"
                variants={fadeInUp}
                transition={{ delay: index * 0.2 }}
              >
                <div className={`bg-gradient-to-r ${milestone.color} w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg`}>
                  <milestone.icon className="w-8 h-8 text-white" />
                </div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 flex-1">
                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-2xl font-bold text-white">{milestone.year}</span>
                    <h3 className="text-xl font-bold text-white">{milestone.title}</h3>
                  </div>
                  <p className="text-slate-300">{milestone.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        {/* Call to Action */}
        <motion.section 
          className="text-center"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          <motion.div 
            className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-sm border border-white/20 rounded-3xl p-12"
            variants={fadeInUp}
          >
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Build Something Amazing?
            </h3>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Let's combine these skills to create exceptional web experiences that users will love.
            </p>
            <motion.button
              className="group bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-2xl hover:shadow-purple-500/25 flex items-center gap-3 mx-auto"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Users className="w-6 h-6" />
              Let's Collaborate
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
        </motion.section>
      </div>
    </div>
  );
};

export default Skills;